/**
 * Encuesta a vendedores (1.0 AdminSellerSurvey): lista de encuestas, resultados por respuesta,
 * alta/edición (pregunta, imagen https, 2–12 respuestas, países, público, activa) y borrado de
 * una respuesta (la persona vuelve a ver la encuesta). Una encuesta activa por país.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@nuvvu/shared/api';
import type { AdminSellerSurveysOutput } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { btn, card, field, muted, Notice, Table, td } from '../lib/ui.js';

type Survey = AdminSellerSurveysOutput['surveys'][number];

interface Form {
  id: string;
  question: string;
  question_image_url: string;
  audience: 'sellers' | 'all';
  options: string;
  countries: string;
  active: boolean;
}
const blank = (): Form => ({
  id: '',
  question: '',
  question_image_url: '',
  audience: 'sellers',
  options: '',
  countries: '',
  active: true,
});
const fromSurvey = (s: Survey): Form => ({
  id: s.id,
  question: s.question,
  question_image_url: '',
  audience: s.audience,
  options: s.options.map((o) => o.label).join('\n'),
  countries: s.countries.join(', '),
  active: s.active,
});
const when = (ms: number | null): string => (ms ? new Date(ms).toLocaleString('es') : '—');

export function SurveyPage(): ReactElement {
  const [data, setData] = useState<AdminSellerSurveysOutput | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (surveyId: string | null): void => {
    call(api.admin.sellerSurveys, surveyId ? { survey_id: surveyId } : {})
      .then(setData)
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  useEffect(() => {
    load(selected);
  }, [selected]);
  const set = (k: keyof Form, v: string | boolean): void => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };
  const save = (): void => {
    if (!form) return;
    const existing = data?.surveys.find((s) => s.id === form.id);
    const labels = form.options
      .split('\n')
      .map((x) => x.trim())
      .filter(Boolean);
    setBusy(true);
    setError(null);
    call(api.admin.saveSellerSurvey, {
      ...(form.id ? { id: form.id } : {}),
      question: form.question,
      ...(form.question_image_url.trim()
        ? { question_image_url: form.question_image_url.trim() }
        : {}),
      audience: form.audience,
      // Conserva el id de las respuestas que ya existían (misma etiqueta) para no perder resultados.
      options: labels.map((label) => {
        const prev = existing?.options.find((o) => o.label === label);
        return prev ? { id: prev.id, label } : { label };
      }),
      countries: form.countries
        .split(/[,\s]+/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean),
      active: form.active,
    })
      .then((r) => {
        setNotice(`Encuesta guardada (${r.id}).`);
        setForm(null);
        setSelected(r.id);
        load(r.id);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const remove = (id: string): void => {
    if (!window.confirm('¿Borrar esta respuesta? La persona volverá a ver la encuesta.')) return;
    call(api.admin.deleteSellerSurveyResponse, { response_id: id })
      .then(() => {
        load(selected);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  const current = data?.surveys.find((s) => s.id === selected);
  const total = Object.values(data?.stats ?? {}).reduce((a, b) => a + b, 0);
  return (
    <>
      <h1 style={{ margin: 0 }}>Encuesta a vendedores</h1>
      <p style={muted}>
        Una pregunta con varias respuestas que ve el usuario (vendedor con anuncio activo, o
        cualquiera) de los países elegidos, una sola vez. Solo una encuesta activa por país: al
        activar una se desactivan las que solapen. Se escribe en el idioma del país.
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      {!data && !error && <p style={muted}>Cargando…</p>}
      {data && (
        <Table head={['Pregunta', 'Público', 'Países', 'Activa', 'Actualizada', '']}>
          {data.surveys.map((s) => (
            <tr key={s.id} style={{ background: s.id === selected ? '#f3f4ff' : undefined }}>
              <td style={td}>{s.question.slice(0, 120)}</td>
              <td style={td}>{s.audience === 'all' ? 'todos' : 'vendedores'}</td>
              <td style={td}>{s.countries.join(', ')}</td>
              <td style={td}>{s.active ? 'sí' : 'no'}</td>
              <td style={td}>{when(s.updated_at)}</td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                <button
                  type="button"
                  style={btn(false)}
                  onClick={() => {
                    setSelected(s.id);
                  }}
                >
                  Resultados
                </button>{' '}
                <button
                  type="button"
                  style={btn(false)}
                  onClick={() => {
                    setForm(fromSurvey(s));
                  }}
                >
                  Editar
                </button>
              </td>
            </tr>
          ))}
          {data.surveys.length === 0 && (
            <tr>
              <td style={td} colSpan={6}>
                Sin encuestas.
              </td>
            </tr>
          )}
        </Table>
      )}
      {!form && (
        <button
          type="button"
          style={{ ...btn(true), justifySelf: 'start' }}
          onClick={() => {
            setForm(blank());
          }}
        >
          Nueva encuesta
        </button>
      )}
      {form && (
        <form
          style={{ ...card, display: 'grid', gap: 10, maxWidth: 640 }}
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <strong>{form.id ? `Editar ${form.id}` : 'Nueva encuesta'}</strong>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Pregunta
            <textarea
              style={{ ...field, minHeight: 70 }}
              value={form.question}
              required
              onChange={(e) => {
                set('question', e.target.value);
              }}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Imagen (URL https, opcional)
            <input
              style={field}
              value={form.question_image_url}
              onChange={(e) => {
                set('question_image_url', e.target.value);
              }}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Respuestas (una por línea, 2–12)
            <textarea
              style={{ ...field, minHeight: 100 }}
              value={form.options}
              onChange={(e) => {
                set('options', e.target.value);
              }}
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Países (ISO-2, separados por comas)
              <input
                style={field}
                value={form.countries}
                placeholder="HU, SK"
                onChange={(e) => {
                  set('countries', e.target.value);
                }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Público
              <select
                style={field}
                value={form.audience}
                onChange={(e) => {
                  set('audience', e.target.value);
                }}
              >
                <option value="sellers">Vendedores con anuncio activo</option>
                <option value="all">Todos los usuarios</option>
              </select>
            </label>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => {
                set('active', e.target.checked);
              }}
            />
            Activa
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" style={btn(true)} disabled={busy}>
              Guardar
            </button>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                setForm(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
      {current && data && (
        <section style={{ ...card, display: 'grid', gap: 10 }}>
          <strong>Resultados: {current.question.slice(0, 120)}</strong>
          <span style={muted}>{total} respuestas</span>
          <Table head={['Respuesta', 'Votos', '%']}>
            {current.options.map((o) => {
              const n = data.stats[o.id] ?? 0;
              return (
                <tr key={o.id}>
                  <td style={td}>{o.label}</td>
                  <td style={td}>{n}</td>
                  <td style={td}>{total ? `${Math.round((n / total) * 100)} %` : '—'}</td>
                </tr>
              );
            })}
          </Table>
          <Table head={['Usuario', 'País', 'Respuesta', 'Fecha', '']}>
            {data.responses.map((r) => (
              <tr key={r.id}>
                <td style={td}>
                  <Link to={`/usuarios/${r.uid}`}>{r.uid.slice(0, 10)}…</Link>
                </td>
                <td style={td}>{r.country}</td>
                <td style={td}>{r.option_label}</td>
                <td style={td}>{when(r.created_at)}</td>
                <td style={td}>
                  <button
                    type="button"
                    style={btn(false, true)}
                    onClick={() => {
                      remove(r.id);
                    }}
                  >
                    Borrar
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        </section>
      )}
    </>
  );
}
